import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { eur, formatApiError } from "@/lib/api";
import { PageHeader, Btn, Modal, Field, inputCls } from "@/components/common";
import { toEur, CURRENCIES, SYMBOL } from "@/lib/expenseConstants";
import { Plus, Pencil, Ban, CheckCircle2, Trash2, GripVertical, Copy } from "lucide-react";

const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const thisYear = () => new Date().getFullYear();
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => { if (!d) return ""; const [y, m, dd] = d.slice(0, 10).split("-"); return `${dd}/${m}/${y}`; };

export default function Affitti() {
  const [year, setYear] = useState(String(thisYear()));
  const [rows, setRows] = useState([]);
  const [rates, setRates] = useState({ EUR: 1, USD: null, MVR: null });
  const [showInactive, setShowInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ description: "", conto_corrente: "", currency: "EUR" });
  const [drafts, setDrafts] = useState({});
  const [dragId, setDragId] = useState(null);

  const load = () => api.get(`/rent-grid?year=${year}`).then((r) => setRows(r.data.rows));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);
  useEffect(() => { api.get("/fx/rates").then((r) => setRates(r.data.rates)); }, []);

  const months = useMemo(() => MONTHS_IT.map((_, i) => `${year}-${String(i + 1).padStart(2, "0")}`), [year]);
  const visibleRows = rows.filter((r) => showInactive || r.active);

  const cellVal = (row, m) => {
    const k = `${row.id}-${m}`;
    if (drafts[k] !== undefined) return drafts[k];
    return row.months?.[m]?.amount ? String(row.months[m].amount) : "";
  };
  const setDraft = (row, m, v) => setDrafts((d) => ({ ...d, [`${row.id}-${m}`]: v }));

  const saveCell = async (row, m) => {
    const k = `${row.id}-${m}`;
    if (drafts[k] === undefined) return;
    const amount = Number(drafts[k]) || 0;
    const prev = row.months?.[m]?.amount || 0;
    if (amount === prev) { setDrafts((d) => { const n = { ...d }; delete n[k]; return n; }); return; }
    try {
      await api.put("/rent-cell", { rent_item_id: row.id, month: m, amount });
      setDrafts((d) => { const n = { ...d }; delete n[k]; return n; });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const rowTotalEur = (row) => months.reduce((a, m) => a + toEur(row.months?.[m]?.amount || 0, row.currency, rates), 0);
  const monthTotalEur = (m) => visibleRows.reduce((a, r) => a + toEur(r.months?.[m]?.amount || 0, r.currency, rates), 0);
  const grandTotal = visibleRows.reduce((a, r) => a + rowTotalEur(r), 0);

  const openNew = () => { setForm({ description: "", conto_corrente: "", currency: "EUR" }); setEditId(null); setOpen(true); };
  const openEdit = (row) => { setForm({ description: row.description || "", conto_corrente: row.conto_corrente || "", currency: row.currency || "EUR" }); setEditId(row.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error("Descrizione richiesta"); return; }
    try {
      if (editId) {
        const row = rows.find((r) => r.id === editId);
        await api.put(`/rent-items/${editId}`, { ...form, active: row?.active ?? true, order: row?.order ?? 0, deactivated_at: row?.deactivated_at || "" });
      } else {
        await api.post("/rent-items", { ...form, active: true });
      }
      toast.success("Salvato"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleActive = async (row) => {
    const willActivate = !row.active;
    try {
      await api.put(`/rent-items/${row.id}`, {
        description: row.description, conto_corrente: row.conto_corrente || "", currency: row.currency || "EUR",
        active: willActivate, order: row.order || 0, deactivated_at: willActivate ? "" : todayStr(),
      });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Eliminare la voce "${row.description}"?`)) return;
    try { await api.delete(`/rent-items/${row.id}`); toast.success("Voce eliminata"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const cur = [...rows];
    const from = cur.findIndex((r) => r.id === dragId);
    const to = cur.findIndex((r) => r.id === targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const [moved] = cur.splice(from, 1);
    cur.splice(to, 0, moved);
    setRows(cur); setDragId(null);
    try { await api.put("/rent-items/reorder", { ids: cur.map((r) => r.id) }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const copyPrevMonth = async (i) => {
    if (i < 1) return;
    const targetYm = months[i]; const prevYm = months[i - 1];
    if (!window.confirm(`Copiare i valori di ${MONTHS_IT[i - 1]} in ${MONTHS_IT[i]} ${year}? I valori esistenti in ${MONTHS_IT[i]} verranno sovrascritti (potrai comunque modificarli dopo).`)) return;
    try {
      for (const row of visibleRows.filter((r) => r.active)) {
        const amt = row.months?.[prevYm]?.amount || 0;
        if (amt > 0) await api.put("/rent-cell", { rent_item_id: row.id, month: targetYm, amount: amt });
      }
      toast.success(`Copiato ${MONTHS_IT[i - 1]} → ${MONTHS_IT[i]}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const years = [thisYear() - 1, thisYear(), thisYear() + 1, thisYear() + 2].map(String);

  return (
    <div>
      <PageHeader title="Affitti" subtitle="Griglia voci × mesi. Inserisci l'importo in ogni cella; trascina per riordinare le voci.">
        <select data-testid="rent-year" value={year} onChange={(e) => setYear(e.target.value)} className={inputCls + " w-auto"}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600 px-2">
          <input type="checkbox" data-testid="rent-show-inactive" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Mostra disattivate
        </label>
        <Btn data-testid="add-rent-item-btn" onClick={openNew}><Plus size={16} /> Aggiungi voce</Btn>
      </PageHeader>

      <div className="mb-4">
        <span className="text-sm text-slate-500">Totale affitti {year} (€): </span>
        <span data-testid="rent-grand-total" className="text-lg font-semibold text-red-600">{eur(grandTotal)}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-auto max-h-[70vh]">
        <table className="text-sm border-collapse">
          <thead>
            <tr className="text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-3 py-2.5 text-left sticky left-0 top-0 bg-slate-50 z-30 min-w-[240px]">Voce</th>
              {MONTHS_IT.map((m, i) => (
                <th key={m} className="px-2 py-2.5 text-right min-w-[80px] sticky top-0 bg-slate-50 z-20">
                  <div className="flex items-center justify-end gap-1">
                    {i >= 1 && <button data-testid={`rent-copy-${i}`} onClick={() => copyPrevMonth(i)} title={`Copia ${MONTHS_IT[i - 1]} in ${m}`} className="p-2 -m-2 md:p-0 md:m-0 text-slate-300 hover:text-sky-600"><Copy size={12} /></button>}
                    {m}
                  </div>
                </th>
              ))}
              <th className="px-3 py-2.5 text-right min-w-[90px] sticky top-0 bg-slate-50 z-20">Totale €</th>
              <th className="px-2 py-2.5 sticky top-0 bg-slate-50 z-20"></th>
            </tr>
          </thead>
          <tbody data-testid="rent-grid-table">
            {visibleRows.map((row) => (
              <tr key={row.id} data-testid={`rent-row-${row.id}`}
                  onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(row.id)}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${!row.active ? "opacity-50" : ""} ${dragId === row.id ? "bg-sky-50" : ""}`}>
                <td className="px-2 py-2 sticky left-0 bg-white z-10">
                  <div className="flex items-start gap-1.5">
                    <span draggable data-testid={`rent-drag-${row.id}`} onDragStart={() => setDragId(row.id)} onDragEnd={() => setDragId(null)}
                          className="cursor-grab text-slate-300 hover:text-slate-500 pt-0.5" title="Trascina per riordinare"><GripVertical size={15} /></span>
                    <div>
                      <div className="font-medium text-slate-800 flex items-center gap-2">
                        {row.description}
                        {!row.active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">disattivata{row.deactivated_at ? ` il ${fmtDate(row.deactivated_at)}` : ""}</span>}
                      </div>
                      <div className="text-xs text-slate-400">{row.conto_corrente ? `C/C: ${row.conto_corrente}` : "—"} · {SYMBOL[row.currency] || row.currency}</div>
                    </div>
                  </div>
                </td>
                {months.map((m) => (
                  <td key={m} className="px-1 py-1 text-right">
                    <input data-testid={`rent-cell-${row.id}-${m}`} value={cellVal(row, m)} disabled={!row.active && !(row.months?.[m]?.amount > 0)}
                      onChange={(e) => setDraft(row, m, e.target.value.replace(/[^0-9.]/g, ""))}
                      onBlur={() => saveCell(row, m)} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      className="w-[72px] text-right tabular rounded border border-transparent hover:border-slate-200 focus:border-sky-500 focus:outline-none px-1.5 py-1 disabled:bg-transparent" placeholder="—" />
                  </td>
                ))}
                <td className="px-3 py-2 text-right tabular font-medium text-slate-700">{eur(rowTotalEur(row))}</td>
                <td className="px-2 py-2">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`rent-edit-${row.id}`} onClick={() => openEdit(row)} className="p-1.5 text-slate-400 hover:text-sky-600" title="Modifica voce"><Pencil size={15} /></button>
                    <button data-testid={`rent-toggle-${row.id}`} onClick={() => toggleActive(row)} className="p-1.5 text-slate-400 hover:text-amber-600" title={row.active ? "Disattiva" : "Riattiva"}>
                      {row.active ? <Ban size={15} /> : <CheckCircle2 size={15} />}
                    </button>
                    <button data-testid={`rent-del-${row.id}`} onClick={() => removeRow(row)} className="p-1.5 text-slate-400 hover:text-red-600" title="Elimina voce"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr><td colSpan={15} className="px-3 py-12 text-center text-slate-400">Nessuna voce. Aggiungi la prima voce di affitto.</td></tr>
            )}
          </tbody>
          {visibleRows.length > 0 && (
            <tfoot>
              <tr className="border-t border-slate-200 bg-slate-50 font-medium text-slate-700">
                <td className="px-3 py-2.5 sticky left-0 bg-slate-50 z-10">Totale mensile (€)</td>
                {months.map((m) => <td key={m} className="px-2 py-2.5 text-right tabular text-xs">{monthTotalEur(m) ? eur(monthTotalEur(m)) : "—"}</td>)}
                <td className="px-3 py-2.5 text-right tabular text-red-600">{eur(grandTotal)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-3">
        Gli importi sono nella valuta della voce; i Totali sono convertiti in € al cambio attuale (1 € = {rates.USD ?? "…"} $ = {rates.MVR ?? "…"} Rf).
        Le voci disattivate non sono modificabili ma i dati storici restano nei report.
      </p>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifica voce affitto" : "Nuova voce affitto"}>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Descrizione"><input data-testid="rent-form-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={inputCls} placeholder="Es. Affitto casa staff" autoFocus /></Field>
          <Field label="Conto corrente"><input data-testid="rent-form-conto" value={form.conto_corrente} onChange={(e) => setForm((f) => ({ ...f, conto_corrente: e.target.value }))} className={inputCls} placeholder="Es. IBAN o nome conto" /></Field>
          <Field label="Valuta">
            <select data-testid="rent-form-currency" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} className={inputCls}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Annulla</Btn>
            <Btn type="submit" data-testid="rent-form-submit">Salva</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
